import React, { useCallback, useEffect, useState } from "react";
import { Ban, Banknote, BellRing, FileText, Smartphone } from "lucide-react";
import { api, ouvrirPdf } from "./api";
import { useConfirmation } from "./Confirmation";
import { Badge, Cellule, Section, Tableau } from "./ui";
import { heures, partDuPrix, regles as reglesDe } from "../lib/reglesPrives";

/* Facturation › Cours privés (Jen, 01.11.2026) : les factures que la console fabrique toute
   seule quand une famille annule son cours privé moins de 24 h avant (50 % du prix). Même
   facture que les autres (QR-facture suisse, PDF joint à l'e-mail d'annulation), sa série
   F-…CP-…, rapprochée du relevé bancaire par sa référence QR (Encaissements) — ou « Payée
   sur place » (cash, TWINT), comme le cours lui-même. « Annuler la facture » : l'école ne
   fait rien payer. Depuis le 01.11.2026 au soir, aussi celles d'une famille absente sans avoir
   annulé (« Absent·e — facturer » : 50 % ou le prix entier).
   Le suivi des impayés (Jen, 02.11.2026 : « un rappel tous les 30 jours ») : envoyée et pas
   payée, la facture passe « À relancer » 30 jours après l'envoi, puis 30 jours après chaque
   rappel. « Rappel de paiement » envoie l'e-mail (gabarit « relance » de l'école, facture QR
   jointe, copie à l'école) — sur un clic, comme tous les rappels ; elle est aussi dans À traiter
   › Factures impayées, avec les autres. Les « 24 h », « 50 % » et « 30 jours » écrits ici sont
   ceux des Réglages › Cours privés (Jen, 02.11.2026), envoyés avec la liste. */

const chf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;
const jour = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const TON = { "Payée": "vert", "Partiellement payée": "bleu", "Annulée": "neutre" };
const LIBELLE = (f) => (f.statut === "Facturée"
  ? (f.facture_envoyee_le ? `Envoyée le ${jour(f.facture_envoyee_le)}` : "Pas encore envoyée")
  : f.statut === "Payée" ? `Payée${f.mode_paiement ? ` · ${f.mode_paiement}` : ""}${f.date_paiement ? ` le ${jour(f.date_paiement)}` : ""}`
    : f.statut === "Annulée" ? `Annulée le ${jour(f.annulee_le)}` : f.statut);
// Où en est le paiement d'une facture partie et pas payée : « À relancer · en retard de 3 j »,
// « relancée il y a 5 j », « pas encore échue — échéance le 02.12 ».
const suivi = (f) => (!["Payée", "Annulée"].includes(f.statut) && f.facture_envoyee_le && f.relance ? f.relance : null);

export default function FacturesPrivees() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const { demander } = useConfirmation();
  const charger = useCallback(() => {
    api("/reservations-privees/factures").then(setD).catch((e) => setErreur(e.message));
  }, []);
  useEffect(charger, [charger]);

  const agir = async (chemin, corps) => {
    setErreur(""); setMessage("");
    try {
      const x = await api(chemin, { method: "POST", body: JSON.stringify(corps || {}) });
      setMessage(x.message); charger();
    } catch (e) { setErreur(e.message); }
  };
  const annuler = async (f) => {
    if (!(await demander({
      titre: `Annuler la facture ${f.no_facture} ?`,
      texte: `${f.client} ne devra plus rien pour ce cours (${chf(f.total_a_facturer)}). La facture reste dans la liste, marquée « Annulée ». Rien n'est envoyé à la famille.`,
      valider: "Annuler la facture",
    }))) return;
    agir(`/reservations-privees/factures/${f.no_facture}/annuler`);
  };

  // « Rappel de paiement » : dû (30 jours après l'envoi ou le dernier rappel), ou « quand même »
  // pour UNE facture pas encore échue ou relancée il y a peu (confirmé, avec la raison).
  const rappeler = async (f) => {
    const r = f.relance || {};
    const quandMeme = !r.due;
    if (!(await demander({
      titre: quandMeme ? `Rappel quand même pour ${f.no_facture} ?` : `Rappel de paiement : ${f.no_facture}`,
      ton: quandMeme ? "attention" : undefined,
      texte: `Un e-mail part à ${f.client} (${f.email}) : le rappel de paiement de l'école, avec la facture jointe (QR pour payer), ${chf(f.solde)} à régler. Une copie arrive à l'école.`,
      points: [
        ...(quandMeme ? [`Pas encore en retard : ${r.motif}.`] : []),
        `S'il n'est toujours pas payé, le prochain rappel sera proposé dans ${d.jours_entre_rappels || d.jours_rappel || 30} jours.`,
      ],
      valider: quandMeme ? "Envoyer quand même" : "Envoyer le rappel",
    }))) return;
    agir(`/reservations-privees/factures/${f.no_facture}/rappel`, { forcer: quandMeme });
  };

  if (!d) return <p className="text-sm text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  const R = reglesDe(d.regles);
  const h = heures(d.annulation_heures || R.annulation_heures);
  const premier = d.jours_premier_rappel || d.jours_rappel || 30;
  const ensuite = d.jours_entre_rappels || d.jours_rappel || 30;
  const tard = partDuPrix(R.annulation_pourcentage);
  const absence = [R.absence_pourcentage, R.absence_pourcentage_max].filter((p, i, t) => t.indexOf(p) === i)
    .map((p) => (Number(p) >= 100 ? "le prix entier" : `${p} %`)).join(" ou ");
  return (
    <Section titre={`Cours privés : annulés moins de ${h} avant, absents`} testid="factures-prives"
      aide={`La famille annule son cours privé moins de ${h} avant (elle-même, ou l'école coche « facturer ${R.ecole_facturer_pourcentage} % » en annulant pour elle) : la console fabrique toute seule la facture de ${tard.texte} et la joint à l'e-mail d'annulation. Absente sans avoir annulé : « Absent·e — facturer » (Cours privés › Réservations), ${absence}. Un virement avec la référence QR se rapproche dans Encaissements ; payée à l'école, « Cash » ou « TWINT ». « Annuler » : l'école ne fait rien payer. Pas payée ${premier} jours après l'envoi : « À relancer » (ici et dans À traiter) ; « Rappel de paiement » envoie le rappel avec la facture jointe, puis la facture revient ${ensuite} jours plus tard si elle n'est toujours pas payée. Aucun rappel ne part sans ton clic.`}>
      {message && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}>{message}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>{erreur}</div>}
      <p className="text-sm" data-testid="factures-prives-resume">
        <strong>{d.ouvertes}</strong> facture{d.ouvertes > 1 ? "s" : ""} en attente de paiement · <strong>{chf(d.a_payer)}</strong> à recevoir
        {d.a_relancer > 0 && <> · <strong style={{ color: "#B91C1C" }} data-testid="factures-prives-a-relancer">{d.a_relancer} à relancer</strong></>}
      </p>
      <Tableau entetes={["Facture", "Famille", { label: "Cours", large: true }, { label: "Montant", droite: true }, "État", ""]}
        lignes={d.factures.map((f) => ({ ...f, id: f.no_facture }))}
        vide={`Aucune facture : aucune famille n'a annulé son cours privé moins de ${h} avant, ni manqué un cours sans annuler.`}
        rendu={(f) => (
          <>
            <Cellule fort>
              <button type="button" className="underline underline-offset-2 whitespace-nowrap inline-flex items-center gap-1" data-testid="factures-prives-pdf"
                onClick={() => ouvrirPdf(`/reservations-privees/factures/${f.no_facture}/pdf`, `Facture ${f.no_facture}`)}>
                <FileText size={13} /> {f.no_facture}
              </button>
            </Cellule>
            <Cellule><div className="text-xs">{f.client}<br /><span className="text-[var(--muted)]">{f.email}</span></div></Cellule>
            <Cellule><span className="text-xs">{f.libelle}</span>{f.eleves && f.eleves !== f.client && <div className="text-[11px] text-[var(--muted)]">pour {f.eleves} · avec {f.prof}</div>}</Cellule>
            <Cellule droite fort>
              <span className={`whitespace-nowrap ${f.statut === "Annulée" ? "line-through text-[var(--muted)]" : ""}`}>{chf(f.statut === "Annulée" ? (f.montant_du ?? f.montant_cours / 2) : f.total_a_facturer)}</span>
              {f.statut === "Partiellement payée" && <div className="text-[11px] font-normal text-[var(--muted)]">reste {chf(f.solde)}</div>}
            </Cellule>
            <Cellule>
              <Badge ton={TON[f.statut] || "ambre"} testid="factures-prives-etat">{LIBELLE(f)}</Badge>
              {suivi(f) && (
                <div className="text-[11px] mt-1" style={{ color: suivi(f).due ? "#B91C1C" : "var(--muted)" }} data-testid="factures-prives-suivi">
                  {suivi(f).due ? "À relancer · " : ""}{suivi(f).badge}
                  {f.relances_nb > 0 && ` · ${f.relances_nb} rappel${f.relances_nb > 1 ? "s" : ""} (dernier le ${jour(f.relance_envoyee_le)})`}
                </div>
              )}
            </Cellule>
            <Cellule droite>
              {!["Payée", "Annulée"].includes(f.statut) && (
                <span className="inline-flex flex-wrap justify-end gap-1">
                  {suivi(f)?.due && (
                    <button type="button" className="btn-primary text-xs" title="Envoyer le rappel de paiement, la facture jointe" data-testid="factures-prives-rappel"
                      onClick={() => rappeler(f)}><BellRing size={12} /> Rappel de paiement</button>
                  )}
                  {suivi(f) && !suivi(f).due && suivi(f).forcable && (
                    <button type="button" className="btn-ghost text-xs" title={suivi(f).motif} data-testid="factures-prives-rappel-quand-meme"
                      onClick={() => rappeler(f)}><BellRing size={12} /> Rappel quand même…</button>
                  )}
                  <button type="button" className="btn-ghost text-xs" title="Payée à l'école en espèces" data-testid="factures-prives-cash"
                    onClick={() => agir(`/reservations-privees/factures/${f.no_facture}/paiement`, { mode: "cash" })}><Banknote size={12} /> Cash</button>
                  <button type="button" className="btn-ghost text-xs" title="Payée à l'école par TWINT" data-testid="factures-prives-twint"
                    onClick={() => agir(`/reservations-privees/factures/${f.no_facture}/paiement`, { mode: "twint" })}><Smartphone size={12} /> TWINT</button>
                  {!(f.montant_recu > 0) && (
                    <button type="button" className="btn-ghost text-xs" title="Ne rien faire payer" data-testid="factures-prives-annuler"
                      onClick={() => annuler(f)}><Ban size={12} /> Annuler</button>
                  )}
                </span>
              )}
            </Cellule>
          </>
        )} />
    </Section>
  );
}
